import type { ComponentType, FC } from "react";
import { useNavigate } from "react-router-dom";

import { LoginModalView, useActiveLoginModalContext } from "@/src/contexts";
import { Button } from "@Components/Button";
import { Link } from "@Components/Link";
import { getHostName, hostName, removeHost } from "@Components/Modules/Helpers";
import type { IMainLoadingModule } from "@Components/Modules/MainLoading";
import { HomeLogin, HomeRegistration } from "@Routes/HomepageLinks";
import { AllStaticPages } from "@Routes/StaticLandingPageLinks";

interface ITransition extends IMainLoadingModule {
    navigateUrl?: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => boolean | void;
    ignoreTransition?: boolean;
}

const Transition: <TProps>(WrappedComponent: ComponentType<TProps>) => FC<TProps & ITransition> = (
    WrappedComponent,
) =>
    function TransitionComponent(props) {
        const navigate = useNavigate();
        const { setActiveLoginModal } = useActiveLoginModalContext();
        const onClick = (event: React.MouseEvent<HTMLAnchorElement>): boolean | void => {
            let result: boolean | void;

            if (event.button === 1) {
                return true;
            }

            if (props.onClick) {
                result = props.onClick(event);
            }

            if (result === false) {
                return false;
            }

            if (props.navigateUrl) {
                if (event.ctrlKey) {
                    return undefined;
                }

                if (navigator.userAgent.toLocaleUpperCase().indexOf("Goodie".toLocaleUpperCase()) > -1) {
                    return true;
                }

                const destinationUrl = new URL(props.navigateUrl, window.location.href);

                switch (destinationUrl.pathname) {
                    case HomeRegistration:
                        setActiveLoginModal(LoginModalView.Registration);
                        event.stopPropagation();
                        return false;
                    case HomeLogin:
                        setActiveLoginModal(LoginModalView.Login);
                        event.stopPropagation();
                        return false;
                    default: {
                        const localDomain = hostName;

                        const destinationDomain = getHostName(props.navigateUrl);

                        if (
                            (destinationDomain && localDomain.localeCompare(destinationDomain) !== 0) ||
                            props.ignoreTransition === true
                        ) {
                            return result;
                        }

                        if (
                            AllStaticPages.find(
                                (link: string) => link === removeHost(props.navigateUrl || ""),
                            )
                        ) {
                            return result;
                        }
                        break;
                    }
                }

                event.preventDefault();
                event.stopPropagation();

                navigate(removeHost(props.navigateUrl));
            }

            return result;
        };

        return (
            <WrappedComponent
                {...props}
                hideMainLoading={!props.navigateUrl || props.hideMainLoading}
                onClick={onClick}
            />
        );
    };

export const TransitionButton = Transition(Button);
export const TransitionLink = Transition(Link);
